import React, { useEffect, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { Project } from '../../store/types'
import { Box } from '@mui/system'
import { Option } from '../Form/Inputs/SelectInput'
import { useForm } from 'react-hook-form'
import { Button, Dialog, FormControl, Typography } from '@mui/material'
import Demand from '../../store/types/models/Demand'
import { useCreateDemandMutation, useEditDemandMutation } from '../../store/redux/reducers/demandSlice'
import { t } from 'i18next'
import { useAppSelector, useNotification } from '../../store/redux/hooks'
import { getCurrentUser, getIsManagement } from '../../store/redux/reducers/currentUserSlice'
import DemandState from '../../store/types/enums/DemandState'
import AutocompleteInput from '../Form/Inputs/AutocompleteInput'
import TextInput from '../Form/Inputs/TextInput'
import { createErrorMessage } from '../../store/utils'
import { useSelector } from 'react-redux'

type VacancyPopupProps = {
  open: boolean
  onClose: () => void
  month: Dayjs
  stateProjects: Project[]
  demandResponse: Demand[]
}

type VacancyPopUpFormValues = {
  project: Option
  people: number
}

const VacancyPopup: React.FC<VacancyPopupProps> = ({ open, onClose, month, stateProjects, demandResponse }) => {
  const currentUser = useAppSelector(state => getCurrentUser(state))
  const options = stateProjects.map(project => ({
    value: project.id,
    label: project.name,
  }))
  const isManagement = useSelector(getIsManagement)
  const [createDemand] = useCreateDemandMutation()
  const [editDemand] = useEditDemandMutation()
  const { displayNotification } = useNotification()

  const [previousPeople, setPreviousPeople] = useState<number | null>(null)

  const handleFormSubmit = async (data: VacancyPopUpFormValues) => {
    const peopleNumber = Number(data.people)
    const demand = demandResponse.find(d => d.project.id === data.project.value)

    if (!demand?.id) {
      handleCreate({ ...data, people: peopleNumber })
    } else {
      handleEdit({ ...data, people: peopleNumber }, demand)
    }
  }

  const handleEdit = async (data: VacancyPopUpFormValues, demand: Demand) => {
    if (!demand.id) {
      return
    }
    try {
      await editDemand({
        id: demand.id,
        demand: {
          numberOfPeople: data.people === 0 ? demand.numberOfPeople : data.people,
          technologies: demand.technologies,
          projectId: data.project?.value,
          submitterId: demand.submitter.id,
          startDate: dayjs(month.utc(true).startOf('month')),
          state: demand.state,
          description: demand.description,
          completed: data.people === 0 ? true : demand.completed,
          version: demand.version,
          vacancy: true,
        },
      }).unwrap()
      displayNotification({ message: t('page.demands.alert.demandEdited'), type: 'success' })
      reset()
      onClose()
    } catch (error) {
      let errorMessage = createErrorMessage(error)
      displayNotification({ message: `${t('message.somethingWentWrong')} ${errorMessage}`, type: 'error' })
    }
  }

  const handleCreate = async (data: VacancyPopUpFormValues) => {
    try {
      await createDemand({
        numberOfPeople: data.people,
        technologies: [],
        projectId: data.project?.value,
        startDate: dayjs(month.utc(true).startOf('month')),
        submitterId: currentUser?.id,
        state: isManagement ? DemandState.INTERNAL : DemandState.PENDING,
        description: null,
        completed: false,
        vacancy: true,
      }).unwrap()
      displayNotification({ message: t('page.demands.alert.demandCreated'), type: 'success' })
      reset()
      onClose()
    } catch (error) {
      let errorMessage = createErrorMessage(error)
      displayNotification({ message: `${t('message.somethingWentWrong')} ${errorMessage}`, type: 'error' })
    }
  }

  const {
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<VacancyPopUpFormValues>({
    defaultValues: {
      people: 0,
    },
    mode: 'onChange',
  })

  useEffect(() => {
    reset()
  }, [open, reset])

  const selectedProject = watch('project')
  const people = watch('people')

  useEffect(() => {
    const demand = selectedProject ? demandResponse.find(d => d.project.id === selectedProject.value) : null

    if (demand) {
      setValue('people', demand.numberOfPeople)
      setPreviousPeople(demand.numberOfPeople)
    } else {
      setValue('people', 0)
      setPreviousPeople(0)
    }
  }, [selectedProject, demandResponse, setValue])

  const isSubmitDisabled = () => {
    return (
      isSubmitting || !selectedProject || isNaN(Number(people)) || Number(people) === previousPeople || !!errors.people
    )
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <Box sx={{ p: 2, minWidth: 400, minHeight: 200 }}>
        <Typography variant="h4" gutterBottom sx={{ my: 2 }}>
          {t('components.vacancyPopup.title')}
        </Typography>
        <FormControl fullWidth sx={{ gap: 4 }}>
          <AutocompleteInput
            options={options}
            name={'project'}
            label={t('components.vacancyPopup.projects')}
            control={control}
          />
          <Box>
            <TextInput
              name={'people'}
              numericType={'natural'}
              control={control}
              label={t('components.vacancyPopup.peopleCount')}
              maxLength={3}
              rules={{
                min: { value: 0, message: t('components.vacancyPopup.errors.minValue') },
                max: { value: 100, message: t('components.vacancyPopup.errors.maxValue') },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0 }}>
            <Button
              sx={{ m: 1 }}
              variant="contained"
              color="secondary"
              onClick={() => {
                reset()
                onClose()
              }}
            >
              {t('components.vacancyPopup.cancel')}
            </Button>
            <Button
              sx={{ m: 1, mr: 0 }}
              variant="contained"
              color="primary"
              disabled={isSubmitDisabled()}
              onClick={handleSubmit(data => {
                handleFormSubmit(data)
                onClose()
              })}
            >
              {t('components.vacancyPopup.save')}
            </Button>
          </Box>
        </FormControl>
      </Box>
    </Dialog>
  )
}

export default VacancyPopup